import { RefObject, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';

import { focusRing } from 'ui/a11y';
import { useFocusTrap } from 'ui/use-focus-trap';

import { BREAKPOINTS, up } from './breakpoints';
import { ChevronIcon } from './icons';
import { AdminNavGroup, AdminNavItem, GUEST_PERMITS_NAV, STATISTICS_NAV, flattenAdminItems } from './nav-items';
import { useDismissable } from './use-dismissable';

const Root = styled.div`
  position: fixed;
  top: 56px; /* xs bar height */
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 40;
  display: flex;

  ${up('sm')} {
    top: 64px; /* sm bar height */
  }

  ${up('md')} {
    /* Safety net: the drawer is only ever mounted below md, but a viewport resize while it's
       open (rather than a fresh close) should never leave it floating over the inline nav — see
       also the matchMedia effect below, which actually closes it on that transition. */
    display: none;
  }
`;

const Scrim = styled.button`
  /* position:absolute (not :fixed) so this starts at Root's own top (below the bar), not at the
     viewport's own top edge — :fixed would ignore Root's offset and dim/intercept clicks on the
     bar itself, including the hamburger/close button that opened this drawer. */
  position: absolute;
  inset: 0;
  border: 0;
  padding: 0;
  background: transparent;
  cursor: default;

  ${up('sm')} {
    background: rgba(51, 42, 35, 0.48); /* artboard-literal scrim colour */
  }

  ${up('md')} {
    display: none;
  }
`;

const Panel = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  background: ${p => p.theme.colors.surface};
  display: flex;
  flex-direction: column;

  ${up('sm')} {
    width: 360px;
    max-width: 100%;
  }
`;

const Nav = styled.nav`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: ${p => p.theme.space[1]} ${p => p.theme.space.threeQuarter};
`;

const sharedRow = `
  display: flex;
  align-items: center;
  min-height: 48px;
  padding: 0 12px;
  border-radius: 8px;
  text-decoration: none;
`;

const TopRow = styled(NavLink)`
  ${sharedRow}
  color: ${p => p.theme.colors.brown};
  font-family: ${p => p.theme.font.family};
  font-size: 17px;
  font-weight: ${p => p.theme.font.weight.bold};

  &[aria-current='page'] {
    background: ${p => p.theme.colors.yellowPale};
  }

  ${focusRing}
`;

const DisclosureButton = styled.button`
  width: 100%;
  ${sharedRow}
  gap: 8px;
  background: transparent;
  border: 0;
  color: ${p => p.theme.colors.brown};
  font-family: ${p => p.theme.font.family};
  font-size: 17px;
  font-weight: ${p => p.theme.font.weight.bold};
  text-align: left;
  cursor: pointer;

  svg {
    transition: transform 0.15s ease;
  }

  &[aria-expanded='true'] svg {
    transform: rotate(180deg);
  }

  ${focusRing}
`;

const DisclosureLabel = styled.span`
  flex-grow: 1;
`;

const FlatList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-left: ${p => p.theme.space[1]};
`;

const FlatItem = styled(NavLink)`
  ${sharedRow}
  gap: ${p => p.theme.space.onePointFive};
  color: ${p => p.theme.colors.brown};
  font-family: ${p => p.theme.font.family};
  font-size: 16px;

  &[aria-current='page'] {
    background: ${p => p.theme.colors.yellowPale};
  }

  ${focusRing}
`;

const FlatItemTile = styled.span`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: ${p => p.theme.colors.yellowPale};
  color: ${p => p.theme.colors.brown};
`;

export interface MobileDrawerProps {
  label: string;
  showGuestPermits: boolean;
  showStatistics: boolean;
  adminGroups: AdminNavGroup[];
  onClose: () => void;
  /** The hamburger button — focus returns there when the drawer closes. */
  returnFocusRef: RefObject<HTMLElement>;
}

/**
 * The xs full-screen / sm 360px-left-sheet-over-a-scrim nav drawer (`V2-Menu-390`). Only ever
 * mounted while open (the caller conditionally renders it), so its own "Administreerimine
 * expanded by default" state always starts fresh.
 */
export function MobileDrawer({
  label,
  showGuestPermits,
  showStatistics,
  adminGroups,
  onClose,
  returnFocusRef,
}: MobileDrawerProps) {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);
  const [adminExpanded, setAdminExpanded] = useState(true);

  useFocusTrap(panelRef, { active: true, returnFocusTo: returnFocusRef });
  useDismissable(true, panelRef, onClose, returnFocusRef);

  // A viewport resize that crosses up into md — where the drawer has no CSS representation at
  // all (Root/Scrim both `display:none` there) and the inline nav takes over instead — must also
  // close it in JS, or the focus trap stays engaged on a drawer nothing can see, stranding
  // keyboard focus. `window.matchMedia` is undefined under jsdom (jest), hence the guard.
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mql = window.matchMedia(`(min-width: ${BREAKPOINTS.md}px)`);
    function handleChange(event: MediaQueryListEvent) {
      if (event.matches) {
        onClose();
      }
    }

    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, [onClose]);

  const flatAdminItems: AdminNavItem[] = flattenAdminItems(adminGroups);

  return (
    <Root data-testid="lis-mobile-drawer">
      <Scrim type="button" aria-hidden="true" tabIndex={-1} onClick={onClose} data-testid="lis-drawer-scrim" />
      <Panel ref={panelRef} role="dialog" aria-modal="true" aria-label={label}>
        <Nav aria-label={label}>
          {showGuestPermits && (
            <TopRow to={GUEST_PERMITS_NAV.to} onClick={onClose}>
              {t(GUEST_PERMITS_NAV.labelKey)}
            </TopRow>
          )}
          {flatAdminItems.length > 0 && (
            <>
              <DisclosureButton
                type="button"
                aria-expanded={adminExpanded}
                aria-controls={adminExpanded ? 'lis-drawer-admin' : undefined}
                onClick={() => setAdminExpanded(v => !v)}
              >
                <DisclosureLabel>{t('header.adminMenu')}</DisclosureLabel>
                <ChevronIcon size={16} />
              </DisclosureButton>
              {adminExpanded && (
                <FlatList id="lis-drawer-admin">
                  {flatAdminItems.map(item => (
                    <FlatItem key={item.id} to={item.to} onClick={onClose}>
                      <FlatItemTile aria-hidden="true">
                        <item.Icon size={18} />
                      </FlatItemTile>
                      <span>{t(item.labelKey)}</span>
                    </FlatItem>
                  ))}
                </FlatList>
              )}
            </>
          )}
          {showStatistics && (
            <TopRow to={STATISTICS_NAV.to} onClick={onClose}>
              {t(STATISTICS_NAV.labelKey)}
            </TopRow>
          )}
        </Nav>
      </Panel>
    </Root>
  );
}

export default MobileDrawer;
